/**
 * ============================================================================
 * SAFE-MUTATE.TS — Wrappers Seguros para Mutacoes no Supabase
 * ============================================================================
 *
 * Proposito: Encapsular chamadas de escrita (INSERT/UPDATE/DELETE) ao Supabase
 *   com tratamento automatico de erros, logging e notificacao ao usuario.
 *
 * Exportacoes principais:
 *   - safeMutate() — Executa mutacao e retorna { data, error } com toast de erro
 *   - safeFire()   — Fire-and-forget: executa mutacao sem retorno de dados
 *
 * Padrao de uso:
 *   const { data, error } = await safeMutate(
 *     () => supabase.from('tasks').update({ status: 'done' }).eq('id', id),
 *     'Erro ao atualizar tarefa'
 *   );
 *
 * Beneficios:
 *   - Elimina try/catch repetitivo nos componentes
 *   - Centraliza logging de erros via logger
 *   - Exibe toast de erro automaticamente via Sonner
 *   - Trata tanto erros do Supabase (result.error) quanto excecoes JS
 * ============================================================================
 */

import { toast } from 'sonner';
import { logger } from '@/lib/logger';

/**
 * Wrapper for Supabase mutations with automatic error handling.
 * Shows a toast on error and returns { data, error } for further handling.
 *
 * Usage:
 *   const { data, error } = await safeMutate(
 *     () => supabase.from('tasks').update({ status: 'done' }).eq('id', taskId),
 *     'Erro ao atualizar tarefa'
 *   );
 *   if (!error) toast.success('Tarefa atualizada!');
 */
export async function safeMutate<T = any>(
  fn: () => PromiseLike<{ data: T; error: any }>,
  errorMessage = 'Erro ao salvar',
): Promise<{ data: T | null; error: any }> {
  try {
    const result = await fn();
    if (result.error) {
      logger.error(errorMessage, result.error);
      toast.error(errorMessage);
      return { data: null, error: result.error };
    }
    return { data: result.data, error: null };
  } catch (err: any) {
    logger.error(errorMessage, err);
    toast.error(errorMessage);
    return { data: null, error: err };
  }
}

/**
 * Fire-and-forget mutation with error toast.
 * Use when you don't need the result.
 *
 * Usage:
 *   await safeFire(
 *     () => supabase.from('notifications').update({ is_read: true }).eq('id', id),
 *     'Erro ao marcar como lida'
 *   );
 */
export async function safeFire(
  fn: () => PromiseLike<{ error: any }>,
  errorMessage = 'Erro ao salvar',
): Promise<boolean> {
  try {
    const { error } = await fn();
    if (error) {
      logger.error(errorMessage, error);
      toast.error(errorMessage);
      return false;
    }
    return true;
  } catch (err: any) {
    logger.error(errorMessage, err);
    toast.error(errorMessage);
    return false;
  }
}
